import { accessRoleLabel, defaultPermissions, parsePermissions, type FinanceAccessRole, type FinancePermissions } from "@/lib/access-permissions";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createClient, type Session, type SupabaseClient, type User } from "@supabase/supabase-js";
import { Clock3, LoaderCircle, LockKeyhole, RefreshCw, ShieldX, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isDuplicateAuthEmailError, isUnconfirmedAuthEmailError, normalizeAuthEmail } from "@/lib/auth-email";
import { requireOwnerApprovalSignup, SignupPolicyError } from "@/lib/signup-policy";

export type AppRole = "admin" | "finance" | "viewer";
export type AccessStatus = "pending" | "active" | "removed";
export type Profile = {
  id: string;
  email: string | null;
  display_name: string | null;
  role: AppRole;
  access_status: AccessStatus;
  finance_role: Exclude<FinanceAccessRole, "owner">;
  permissions: FinancePermissions;
  policy_realtime?: boolean;
};

type AuthContextValue = {
  configured: boolean;
  client: SupabaseClient | null;
  user: User | null;
  profile: Profile | null;
  canEdit: boolean;
  isOwner: boolean;
  accessLabel: string;
  permissions: FinancePermissions;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();
const configured = Boolean(supabaseUrl && supabaseKey);
const client = configured ? createClient(supabaseUrl, supabaseKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
}) : null;

const AuthContext = createContext<AuthContextValue>({
  configured: false,
  client: null,
  user: null,
  profile: null,
  canEdit: true,
  isOwner: true,
  accessLabel: "Owner",
  permissions: defaultPermissions("owner"),
  refreshProfile: async () => undefined,
  signOut: async () => undefined,
});

async function readProfile(userId: string) {
  if (!client) return null;
  const { data, error } = await client.rpc("finance_access_snapshot_v0197");
  if (error) throw new Error("Hak akses belum dapat dimuat. Periksa migrasi v0.19.7 dan koneksi.");
  if (!data || data.id !== userId) throw new Error("Access snapshot unavailable; apply migration v0.19.7");
  return { ...data, permissions: parsePermissions(data.permissions) } as Profile;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(configured);
  const [error, setError] = useState("");
  const profileRef = useRef<Profile | null>(null);
  const userIdRef = useRef<string | null>(null);
  const profileRequestRef = useRef<string | null>(null);
  const accessSequenceRef = useRef(0);

  useEffect(() => {
    if (!client) return;
    let active = true;
    const loadProfile = (nextSession: Session, blocking: boolean) => {
      const userId = nextSession.user.id;
      if (profileRequestRef.current === userId) return;
      profileRequestRef.current = userId;
      const sequence = ++accessSequenceRef.current;
      if (blocking) setLoading(true);
      window.setTimeout(() => {
        readProfile(userId)
          .then((value) => {
            if (!active || userIdRef.current !== userId || sequence !== accessSequenceRef.current) return;
            profileRef.current = value;
            setProfile(value);
            setError("");
          })
          .catch((profileError) => {
            if (active && sequence === accessSequenceRef.current) setError(profileError instanceof Error ? profileError.message : "Profile unavailable");
          })
          .finally(() => {
            if (profileRequestRef.current === userId) profileRequestRef.current = null;
            if (active && userIdRef.current === userId && sequence === accessSequenceRef.current) setLoading(false);
          });
      }, 0);
    };

    void client.auth.getSession().then(({ data, error: sessionError }) => {
      if (!active) return;
      if (sessionError) setError(sessionError.message);
      const initialSession = data.session;
      setSession(initialSession);
      userIdRef.current = initialSession?.user.id || null;
      if (initialSession) loadProfile(initialSession, true);
      else setLoading(false);
    });

    const { data: subscription } = client.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return;
      if (!nextSession) {
        accessSequenceRef.current++;
        userIdRef.current = null;
        profileRef.current = null;
        profileRequestRef.current = null;
        setSession(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      const nextUserId = nextSession.user.id;
      const userChanged = userIdRef.current !== null && userIdRef.current !== nextUserId;
      userIdRef.current = nextUserId;
      setSession(nextSession);

      // TOKEN_REFRESHED and repeated SIGNED_IN events only replace the session.
      // Keeping the profile mounted prevents a full dashboard remount/refetch.
      if (!userChanged && profileRef.current?.id === nextUserId) return;
      if (userChanged) {
        profileRef.current = null;
        setProfile(null);
      }
      loadProfile(nextSession, !profileRef.current);
    });
    return () => { active = false; subscription.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!client || !session?.user.id) return;
    const userId = session.user.id;
    let active = true;

    const applyProfile = (value: Partial<Profile>) => {
      if (!active || userIdRef.current !== userId) return;
      const next = { ...profileRef.current, ...value } as Profile;
      if (next.id !== userId || !next.access_status) {
        void refreshAccess();
        return;
      }
      if (JSON.stringify(profileRef.current) === JSON.stringify(next)) return;
      profileRef.current = next;
      setProfile(next);
      setError("");
    };
    const refreshAccess = async (blocking = false) => {
      if (!active || userIdRef.current !== userId) return;
      const sequence = ++accessSequenceRef.current;
      try {
        const value = await readProfile(userId);
        if (value && sequence === accessSequenceRef.current) applyProfile(value);
      } catch (profileError) {
        if (active && sequence === accessSequenceRef.current) {
          if (blocking) { profileRef.current = null; setProfile(null); }
          setError(profileError instanceof Error ? profileError.message : "Profile unavailable");
        }
      } finally { if (active && sequence === accessSequenceRef.current) setLoading(false); }
    };

    const refreshChangedAccess = (clear: boolean) => {
      if (!active || userIdRef.current !== userId) return;
      if (clear) setLoading(true);
      void refreshAccess(clear);
    };
    const channel = client
      .channel(`finance-profile-${userId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${userId}` },
        (payload) => {
          const next = payload.new as Partial<Profile>, previous = profileRef.current;
          const changed = !previous || next.role !== previous.role || next.finance_role !== previous.finance_role || next.access_status !== previous.access_status;
          refreshChangedAccess(changed);
        },
      );
    if (profile?.policy_realtime) channel.on("postgres_changes", { event: "UPDATE", schema: "public", table: "finance_role_policies" }, (payload) => {
        if (profileRef.current?.role !== "admin" && payload.new.role === profileRef.current?.finance_role) refreshChangedAccess(true);
      });
    channel.subscribe();

    // Realtime, focus and the existing access-only interval refresh the snapshot.
    // Identical snapshots keep the dashboard mounted; changed permissions
    // remount it to discard rows and panels from the previous access scope.
    const onFocus = () => { void refreshAccess(); };
    window.addEventListener("focus", onFocus);
    const interval = window.setInterval(() => void refreshAccess(), 60_000);

    return () => {
      active = false;
      window.removeEventListener("focus", onFocus);
      window.clearInterval(interval);
      void client?.removeChannel(channel);
    };
  }, [session?.user.id, Boolean(profile?.policy_realtime)]);

  useEffect(() => {
    if (!client || !session || profile?.access_status !== "removed") return;
    setError("Akses akun ini telah dicabut oleh Owner.");
    void client.auth.signOut({ scope: "local" });
  }, [session?.user.id, profile?.access_status]);

  const value = useMemo<AuthContextValue>(() => {
    const isActive = !configured || profile?.access_status === "active";
    const isOwner = !configured || (isActive && profile?.role === "admin");
    return {
      configured,
      client,
      user: session?.user || null,
      profile,
      canEdit: isOwner,
      isOwner,
      accessLabel: accessRoleLabel(isOwner ? "owner" : profile?.finance_role || "user"),
      permissions: !configured ? defaultPermissions("owner") : profile?.permissions || parsePermissions(null),
      refreshProfile: async () => {
        if (!client || !session) return;
        const sequence = ++accessSequenceRef.current;
        try {
          const value = await readProfile(session.user.id);
          if (sequence === accessSequenceRef.current) { profileRef.current = value; setProfile(value); setLoading(false); setError(""); }
        } catch {
          if (sequence === accessSequenceRef.current) setError("Status persetujuan belum dapat diperiksa. Coba lagi setelah koneksi tersedia.");
        }
      },
      signOut: async () => { if (client) await client.auth.signOut(); },
    };
  }, [session, profile]);

  if (!configured) return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  if (loading) return <FullPageMessage icon={<LoaderCircle className="size-6 animate-spin" />} title="Memuat EventKita Finance" detail="Memeriksa sesi dan hak akses…" />;
  if (!session) return <Login error={error} onError={setError} />;
  if (!profile) return <FullPageMessage icon={<LockKeyhole className="size-6" />} title="Profil belum tersedia" detail={error || "Profil akun belum dapat dibaca. Pastikan migrasi v0.19.7 sudah dijalankan, lalu coba lagi."} action={<div className="flex justify-center gap-2"><Button variant="outline" onClick={() => void client?.auth.signOut()}>Keluar</Button><Button onClick={() => window.location.reload()}><RefreshCw />Coba lagi</Button></div>} />;
  if (profile.access_status === "pending") return <FullPageMessage icon={<Clock3 className="size-6" />} title="Menunggu persetujuan Owner" detail={error || "Permintaan sudah masuk ke dashboard Owner. Tidak perlu verifikasi email. Dashboard terbuka setelah akses disetujui."} action={<div className="flex justify-center gap-2"><Button variant="outline" onClick={() => void client?.auth.signOut()}>Keluar</Button><Button onClick={() => void value.refreshProfile()}><RefreshCw />Periksa status</Button></div>} />;
  if (profile.access_status === "removed") return <FullPageMessage icon={<ShieldX className="size-6" />} title="Akses telah dicabut" detail="Akun tetap tersimpan untuk menjaga histori, tetapi tidak lagi dapat membaca data EventKita Finance. Hubungi Owner bila akses perlu dipulihkan." action={<Button variant="outline" onClick={() => void client?.auth.signOut()}>Kembali ke login</Button>} />;
  return <AuthContext.Provider key={JSON.stringify([profile.id, profile.role, profile.finance_role, profile.permissions])} value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

function Login({ error, onError }: { error: string; onError: (value: string) => void }) {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [registrationSent, setRegistrationSent] = useState<string | null>(null);

  function switchMode(nextMode: "login" | "signup") {
    setMode(nextMode);
    setPassword("");
    setConfirmation("");
    onError("");
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!client || busy) return;
    const normalizedEmail = normalizeAuthEmail(email);
    setEmail(normalizedEmail);
    onError("");
    if (mode === "signup") {
      if (!displayName.trim()) { onError("Nama perlu diisi."); return; }
      if (password.length < 8) { onError("Password minimal 8 karakter."); return; }
      if (password !== confirmation) { onError("Konfirmasi password belum sama."); return; }
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        await requireOwnerApprovalSignup(supabaseUrl!, supabaseKey!);
        const { data, error: signupError } = await client.auth.signUp({
          email: normalizedEmail,
          password,
          options: { data: { display_name: displayName.trim() } },
        });
        const duplicate = signupError && isDuplicateAuthEmailError(signupError)
          || (!data.session && data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0);
        if (duplicate) {
          setMode("login"); setPassword(""); setConfirmation("");
          onError("Email sudah terdaftar. Silakan masuk; akun yang menunggu persetujuan tidak perlu didaftarkan ulang.");
        } else if (signupError) onError(signupError.message);
        else if (!data.session && data.user?.id) {
          setPassword(""); setConfirmation(""); setRegistrationSent(normalizedEmail);
        } else if (!data.session) onError("Hasil pendaftaran belum dapat dipastikan. Hubungi Owner sebelum mencoba daftar ulang.");
        else { setPassword(""); setConfirmation(""); }
      } else {
        const { error: loginError } = await client.auth.signInWithPassword({ email: normalizedEmail, password });
        if (loginError) onError(isUnconfirmedAuthEmailError(loginError)
          ? "Akun ini masih memerlukan penanganan Owner. Tidak perlu membuka tautan verifikasi atau daftar ulang; hubungi Owner untuk memulihkan akses."
          : loginError.message);
        else setPassword("");
      }
    } catch (requestError) {
      onError(requestError instanceof SignupPolicyError ? requestError.message : "Permintaan belum dapat diselesaikan. Periksa koneksi lalu coba lagi.");
    } finally { setBusy(false); }
  }

  if (registrationSent) return <FullPageMessage icon={<Clock3 className="size-6" />} title="Menunggu persetujuan Owner"
    detail={`Permintaan untuk ${registrationSent} sudah tercatat. Tunggu persetujuan Owner, lalu masuk dengan email dan password. Tidak perlu membuka tautan verifikasi atau daftar ulang.`}
    action={<Button onClick={() => { setRegistrationSent(null); switchMode("login"); }}>Ke halaman masuk</Button>} />;

  return (
    <main className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_18%_0%,rgba(217,70,239,.18),transparent_36%),radial-gradient(circle_at_90%_90%,rgba(139,92,246,.13),transparent_32%)] p-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border bg-card p-5 shadow-xl shadow-fuchsia-950/10 sm:p-7">
        <div className="mb-7 flex items-center gap-3"><img src="/eventkita-logo.webp" alt="EventKita" className="h-20 w-16 shrink-0 rounded-lg bg-white p-1.5 object-contain" /><div><h1 className="font-semibold">EventKita Finance</h1><p className="text-sm text-muted-foreground">Masuk untuk mengakses data keuangan</p></div></div>
        <div className="mb-5 grid grid-cols-2 rounded-xl bg-muted p-1" aria-label="Pilihan autentikasi"><Button type="button" variant={mode === "login" ? "secondary" : "ghost"} className="rounded-lg" disabled={busy} onClick={() => switchMode("login")}>Masuk</Button><Button type="button" variant={mode === "signup" ? "secondary" : "ghost"} className="rounded-lg" disabled={busy} onClick={() => switchMode("signup")}><UserPlus />Daftar</Button></div>
        {mode === "signup" && <label className="mb-4 block"><span className="mb-2 block text-sm font-medium">Nama</span><Input autoComplete="name" required value={displayName} disabled={busy} onChange={(event) => setDisplayName(event.target.value)} className="min-h-11" placeholder="Nama pengguna" /></label>}
        <label className="block"><span className="mb-2 block text-sm font-medium">Email</span><Input type="email" autoComplete="email" required value={email} disabled={busy} onChange={(event) => setEmail(event.target.value)} onBlur={() => setEmail(normalizeAuthEmail)} className="min-h-11" /></label>
        <label className="mt-4 block"><span className="mb-2 block text-sm font-medium">Password</span><Input type="password" autoComplete={mode === "signup" ? "new-password" : "current-password"} required minLength={mode === "signup" ? 8 : undefined} value={password} disabled={busy} onChange={(event) => setPassword(event.target.value)} className="min-h-11" /></label>
        {mode === "signup" && <label className="mt-4 block"><span className="mb-2 block text-sm font-medium">Ulangi password</span><Input type="password" autoComplete="new-password" required minLength={8} value={confirmation} disabled={busy} onChange={(event) => setConfirmation(event.target.value)} className="min-h-11" /></label>}
        {error && <p role="alert" className="mt-4 rounded-xl border border-red-500/25 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
        <Button type="submit" className="mt-6 min-h-11 w-full" disabled={busy}>{busy && <LoaderCircle className="size-4 animate-spin" />}{busy ? (mode === "signup" ? "Mendaftarkan…" : "Memeriksa…") : (mode === "signup" ? "Buat akun" : "Masuk")}</Button>
        <p className="mt-4 text-center text-xs leading-5 text-muted-foreground">{mode === "signup" ? "Daftar dengan email dan password, lalu tunggu persetujuan Owner. Tidak perlu verifikasi email." : "Hak akses dikelola oleh Owner EventKita Finance."}</p>
      </form>
    </main>
  );
}

function FullPageMessage({ icon, title, detail, action }: { icon: React.ReactNode; title: string; detail: string; action?: React.ReactNode }) {
  return <main className="flex min-h-screen items-center justify-center p-4"><div className="max-w-sm rounded-2xl border bg-card p-7 text-center shadow-sm"><span className="mx-auto mb-4 flex size-12 items-center justify-center rounded-2xl bg-muted text-primary">{icon}</span><h1 className="font-semibold">{title}</h1><p className="mt-2 text-sm text-muted-foreground">{detail}</p>{action && <div className="mt-5">{action}</div>}</div></main>;
}
